import {RepositoryPicker} from '@github-ui/item-picker/RepositoryPicker'
import type {RepositoryPickerRepository$data as Repository} from '@github-ui/item-picker/RepositoryPickerRepository.graphql'
import type {RepositoryPickerTopRepositoriesQuery} from '@github-ui/item-picker/RepositoryPickerTopRepositoriesQuery.graphql'

import {Box, Text} from '@primer/react'
import {Suspense, useId} from 'react'
import {graphql, type PreloadedQuery} from 'react-relay'
import {TemplateListPane} from './TemplateListPane'
import type {IssueCreatePayload} from './utils/model'
import {TemplateListLoading} from './TemplateList'

export const RepositoryTemplates = graphql`
  query RepositoryAndTemplatePickerDialogQuery($id: ID!) {
    node(id: $id) {
      ... on Repository {
        # eslint-disable-next-line relay/must-colocate-fragment-spreads This will be fixed once the itempickers are not using @inline
        ...RepositoryPickerRepositoryIssueTemplates
      }
    }
  }
`

export type RepositoryAndTemplatePickerDialogProps = {
  repository: Repository | undefined
  setRepository: (repository: Repository | undefined) => void
  topReposQueryRef: PreloadedQuery<RepositoryPickerTopRepositoriesQuery>
  organization?: string
  onTemplateSelected: (template: IssueCreatePayload) => void
}

export function RepositoryAndTemplatePickerDialog({
  repository,
  setRepository,
  topReposQueryRef,
  organization,
  onTemplateSelected,
}: RepositoryAndTemplatePickerDialogProps) {
  const descriptionId = useId()
  return (
    <Box>
      <Field name="Repository" />
      <Box sx={{width: 'content', mb: 2}}>
        <RepositoryPicker
          aria-describedby={descriptionId}
          initialRepository={repository}
          onSelect={setRepository}
          organization={organization}
          topReposQueryRef={topReposQueryRef}
          focusRepositoryPicker={true}
          enforceAtleastOneSelected={true}
          options={{hasIssuesEnabled: true}}
        />
      </Box>
      <Field name="Templates and forms" />
      <Suspense fallback={<TemplateListLoading />}>
        {repository && (
          <TemplateListPane
            repository={repository}
            onTemplateSelected={onTemplateSelected}
            descriptionId={descriptionId}
          />
        )}
      </Suspense>
    </Box>
  )
}

function Field({name}: {name: string}) {
  return (
    <Box sx={{pt: 1, pb: 1}}>
      <Text sx={{fontWeight: 'bold'}}>{name}</Text>
      <Text sx={{color: 'danger.fg', ml: 1, mb: 1}}>*</Text>
    </Box>
  )
}

try{ RepositoryAndTemplatePickerDialog.displayName ||= 'RepositoryAndTemplatePickerDialog' } catch {}
try{ Field.displayName ||= 'Field' } catch {}